import React, { useEffect, useMemo, useRef, useState, FormEvent } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import arrowOutlined from "@/assets/arrow-outlined.svg";
import { Url } from "@/helpers/url";
import { useServices } from "@/services-context";
import styles from "./signup-page.module.scss";
import fonts from "@/style/fonts.module.scss";
import EnterHandler from "@/elements-react/enter-handler";
import FixCheckbox from "@/elements-react/fix-checkbox";
import { FooterElement } from "@/elements-react/footer-element/FooterElement";
import { FixLink } from "@/elements-react/fix-link";
import { FixButton } from "@/elements-react/fix-button";
import { Input } from "@/uikit";
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { Icon } from "@/elements-react/fix-icon";
import { Gtag } from "@/services/gtag.ts";
import { pageSuspense, useSuspenseLoader } from "@/helpers/suspense";
import { LocationHook, RouteHook, useLocation, useRoute } from "preact-iso";
import { Services } from "@/services/services";
import { googleEvent } from "@/helpers/google-events.ts";
import { FixDropdown, FixDropdownOptionItem } from "@/elements-react/fix-dropdown";
import { COUNTRIES_LISTENER_NAME } from "@/services/countries";
import { PARAMETERS_LISTENER_NAME } from "@/services/parameters";
import { CUSTOMER_CHANNEL_LISTENER_NAME } from "@/services/customer-channel";
import {supportedLanguages} from "@/constants/languages.ts";
import {Header} from "@/elements-react/catalog-layout/header";

const fetchPageData = async (params: { services: Services; location: LocationHook; route: RouteHook }) => {
    const { services, location, route } = params;
    await services.initialize.prepare({ location, route });
};

const SignupPage = pageSuspense(() => {
    const route = useRoute();
    const location = useLocation();
    const services = useServices();

    useSuspenseLoader({
        key: "signup-route",
        loader: () => fetchPageData({ services, location, route })
    });

    const { parameters, countries, customerChannel } = useServices([
        COUNTRIES_LISTENER_NAME,
        PARAMETERS_LISTENER_NAME,
        CUSTOMER_CHANNEL_LISTENER_NAME,
    ]);

    const currentCurrencyCode = parameters.getVisibleCurrency();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const { layoutType, defaultLayoutStyleType } = useLayoutType();
    const formRef = useRef<HTMLFormElement>(null);
    const initialCountrySet = useRef(false);
    const currentCountry = parameters.getVisibleCountry();
    const currentLang = parameters.getVisibleLanguage();
    const locale = supportedLanguages?.find((l: { code: string; }) => l.code === currentLang)?.id ?? null;
    const REQUIRED_MSG = t("cart.form.pleaseFillField", "Please fill out this field");


    const [formData, setFormData] = useState({
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        subscribeNewsletter: false,
        countryId: null as string | null,
        currencyCode: currentCurrencyCode as string | null,
        locale: locale as string | null,
        isCompany: false,
        companyName: "",
        registrationNumber: "",
        vatNumber: "",
    });

    const countryOptions: FixDropdownOptionItem[] = useMemo(
        () =>
            ((countries.getChannelCountries() ?? []) as { id: string; code: string; name: string }[])
                .map((c) => ({
                    value: c.id,
                    label: c.name,
                    selected: c.code === currentCountry,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [customerChannel.availableCountries, currentCountry]
    );

    useEffect(() => {
        const code = currentCountry;
        if (!code) return;
        if (!services.countries?.allCountriesIsLoaded) return;
        if (initialCountrySet.current) return;
        if (formData.countryId) {
            initialCountrySet.current = true;
            return;
        }

        const detected = services.countries.getByCode(code);
        if (detected) {
            setFormData((prev) => ({ ...prev, countryId: detected.id }));
            initialCountrySet.current = true;
        }
    }, [
        services.parameters?.detectedParameters?.country,
        services.countries?.allCountriesIsLoaded,
        formData.countryId,
        services.countries,
    ]);

    useEffect(() => {
        if (services.customerChannel.customer) {
            Url.route(Url.to("home-page-with-lang"));
        }
        // eslint-disable-next-line
    }, [services.customerChannel.customer]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target as HTMLInputElement;

        let error = "";
        if (name === "email") {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (value && !emailRegex.test(value)) {
                error = t("x926694e5jfby7au.email-incorrect", "Please enter a valid email address");
            }
        }
        if (name === "confirmPassword") {
            if (value && value !== formData.password) {
                error = t("a1.passwords-do-not-match", "Passwords do not match");
            }
        }

        setFormErrors((prev) => ({ ...prev, [name]: error }));
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleClear = (name: keyof typeof formData) => {
        setFormData((prev) => ({ ...prev, [name]: "" as any }));
        setFormErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleBusinessChange = (e: { value: boolean; name: string }) => {
        setFormData((prev) => ({ ...prev, isCompany: e.value }));
    };

    const validateBeforeSubmit = (): boolean => {
        const errs: Record<string, string> = {};

        ["email", "password", "phone", "countryId"].forEach((key) => {
            if (!formData[key as keyof typeof formData]) errs[key] = REQUIRED_MSG;
        });

        if (formData.isCompany) {
            ["companyName", "registrationNumber"].forEach((key) => {
                if (!formData[key as keyof typeof formData]) errs[key] = REQUIRED_MSG;
            });
        }

        if (formData.confirmPassword !== formData.password) {
            errs.confirmPassword = t("a1.passwords-do-not-match", "Passwords do not match");
        }

        setFormErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e?: FormEvent) => {
        if (e) e.preventDefault();
        if (!validateBeforeSubmit()) return;

        const {
            email, password, phone, subscribeNewsletter,
            countryId, currencyCode, locale,
            isCompany, companyName, registrationNumber, vatNumber,
        } = formData;

        setLoading(true);

        const signupResult = await services.customerChannel.signup({
            email,
            password,
            phone,
            countryId,
            currencyCode,
            locale,
            isCompany,
            companyName,
            registrationNumber,
            vatNumber,
            newsletter: subscribeNewsletter,
        });

        if (!signupResult?.success) {
            setLoading(false);
            setFormErrors(signupResult?.errors || {});
            return;
        }

        await services.customerChannel.login({ email, password });
        await services.customerChannel.fetchCustomerChannel();
        services.cart.fetchCart();

        if (isCompany) Gtag?.pushEvent?.("create_business_account", "");
        Gtag?.pushEvent?.("save_about_yourself_click", "");

        Url.route(Url.to("home-page-with-lang"));
    };

    const policyLink = Url.to("privacy-policy-page");
    const elementsType =
        layoutType === LayoutTypes.TYPE_DESKTOP ? LayoutStyleTypes.TYPE_MD : LayoutStyleTypes.TYPE_SM;

    const renderContinueShoppingLink = () => (
        <FixLink
            className="continueShoppingLink"
            href={Url.to("home-page-with-lang")}
            type={elementsType}
            onClick={() => googleEvent("continue_shopping_click")}
        >
            <Icon src={arrowOutlined} />
            <span>{t("k0q7l2w2q1856v4o.continue-shopping", "Continue shopping")}</span>
        </FixLink>
    );

    return (
        <div className={classNames(styles.root)}>
            <Header simple={true}/>

            <div className={classNames(styles[defaultLayoutStyleType])}>
                <div className={classNames(styles.signupPageContainer)}>
                    {layoutType === LayoutTypes.TYPE_MOBILE && (
                        <span className={classNames(styles.continueShoppingContainer)}>
              {renderContinueShoppingLink()}
            </span>
                    )}

                    <div className={styles.signupContainer}>
                        <EnterHandler onEnter={handleSubmit}>
                            <form className={styles.signupForm} name="signup" ref={formRef} onSubmit={handleSubmit}>
                                <div>
                  <h1 className={classNames(styles.signupHeading, fonts["heading-2"])}>
                    {t("ya79u75dr5ondfid.sign-up", "Sign Up")}
                  </h1>
                                </div>

                                <div className={styles.inputsContainer}>
                                    <Input
                                        name="email"
                                        label={t("a2.email-address", "Email address")}
                                        type="email"
                                        placeholder="customer@email.com"
                                        customError={formErrors.email}
                                        required
                                        autoFocus={true}
                                        clearable
                                        value={formData.email}
                                        onChange={handleChange}
                                        onInput={handleChange}
                                        onClear={() => handleClear("email")}
                                    />

                                    <Input
                                        name="phone"
                                        type="text"
                                        label={t("87c03r37rm8a2tms.phone-number","Phone number")}
                                        placeholder="+12025550147..."
                                        customError={formErrors.phone}
                                        value={formData.phone}
                                        onChange={handleChange}
                                        clearable
                                        required
                                        onClear={() => handleClear("phone")}
                                    />

                                    <FixDropdown
                                        items={countryOptions}
                                        label={t("nhuz8dp4nq7nrm9y.select-country", "Select your country")}
                                        placeholder={t("nhuz8dp4nq7nrm9y.select-country", "Select your country")}
                                        isSearchable
                                        onSelect={(value: string | number) => {
                                            setFormData((p) => {
                                                return ({
                                                    ...p,
                                                    countryId: value,
                                                });
                                            });
                                        }}
                                    />

                                    <div className={styles.divider}></div>

                                    <div  className={classNames(styles.passwordRow, {
                                        [styles.passwordRowHorizontal]: layoutType === LayoutTypes.TYPE_DESKTOP,
                                        [styles.passwordRowVertical]: layoutType !== LayoutTypes.TYPE_DESKTOP,
                                    })}>
                                        <Input
                                            name="password"
                                            type="password"
                                            label={t( "c3m6uuwc048svqa5.password", "Create a password")}
                                            placeholder={t("a4.min-8-characters","Min 8 characters")}
                                            customError={formErrors.password}
                                            required
                                            value={formData.password}
                                            onChange={handleChange}
                                            clearable
                                            onClear={() => handleClear("password")}
                                        />
                                        <Input
                                            name="confirmPassword"
                                            type="password"
                                            label={t("a3.confirm-password", "Confirm password")}
                                            placeholder={t("a5.repeat-password", "Repeat password")}
                                            customError={formErrors.confirmPassword}
                                            required
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            clearable
                                            onClear={() => handleClear("confirmPassword")}
                                        />
                                    </div>

                                    <div className={styles.divider}></div>

                                </div>

                                <div className={styles.businessToggle}>
                                    <FixCheckbox name="isCompany" checked={formData.isCompany} onChange={handleBusinessChange}>
                                        {t("a6.is-business-account", "It's business account")}
                                    </FixCheckbox>
                                </div>

                                <div
                                    className={classNames(
                                        styles.businessSection,
                                        formData.isCompany ? styles.expanded : styles.collapsed
                                    )}
                                    aria-hidden={!formData.isCompany}
                                >
                                    <div className={styles.inputsContainer}>
                                        <Input
                                            name="companyName"
                                            type="text"
                                            label={t("a7.company-name", "Company name")}
                                            placeholder={t("a8.company-name-example", "e.g. Fixparts SIA")}
                                            customError={formErrors.companyName}
                                            value={formData.companyName}
                                            onChange={handleChange}
                                            clearable
                                            onClear={() => handleClear("companyName")}
                                        />

                                        <Input
                                            name="registrationNumber"
                                            type="text"
                                            label={t("a9.registration-number", "Registration number")}
                                            placeholder={t("a10.registration-number-example", "e.g. 40103212345")}
                                            customError={formErrors.registrationNumber}
                                            value={formData.registrationNumber}
                                            onChange={handleChange}
                                            clearable
                                            onClear={() => handleClear("registrationNumber")}
                                        />

                                        <Input
                                            name="vatNumber"
                                            type="text"
                                            label={t("a11.vat-number", "VAT number (optional)")}
                                            placeholder={t("a12.vat-number-example", "e.g. LV12345678901")}
                                            customError={formErrors.vatNumber}
                                            value={formData.vatNumber}
                                            onChange={handleChange}
                                            clearable
                                            onClear={() => handleClear("vatNumber")}
                                        />
                                    </div>
                                </div>

                                <FixButton onClick={handleSubmit} variant="primary" type="submit" fullWidth loading={loading}>
                                    {formData.isCompany
                                        ? t("a13.register-as-business", "Register as a business customer")
                                        : t("ovd6x5p1j74vsi7u.create-account", "Create your account")}
                                </FixButton>
                            </form>
                        </EnterHandler>

                        <div className={styles.divider}></div>

                        <div className={styles.termsBlock}>
                            <div className={styles.secured}>
                              <Icon name="secured"/>
                                <span>
                                    {formData.isCompany ? t("a14.business-data-secure","Your business data is secure and used only for verification") : t("a784gfbairsbsu9q.your-data-is-secure", "Your data is secure and never shared")}
                                </span>
                            </div>

                            <span className={classNames(styles.firstLine)}>
                                {t("7nirh7mrk4ou46rz.agree-policy-text", "By creating an account you are agreeing to our")}
                            </span>

                            <FixLink
                                className={styles.policyLink}
                                href={policyLink}
                                type={LayoutStyleTypes.TYPE_SM}
                                onClick={() => googleEvent("privacy_policy_click")}
                            >
                                {t("i0jm43p91f8qqk3w.policy-link", "Privacy policy and terms of use")}
                            </FixLink>
                        </div>
                    </div>
                </div>
            </div>

            {layoutType === LayoutTypes.TYPE_DESKTOP && <FooterElement />}
        </div>
    );
});

export { SignupPage };